import dayjs from 'dayjs'
import { AvariaEnum } from '../types'
import { dropdownOptions } from './components/ReceivementTables/ReceivementPurchaseTable/ActionDropdown'
import { History } from 'history'
import { PrintInvoicesStepsEnum } from '../supplier/components/PrintInvoicesSection/PrintInvoicesSection.types'
import { RequestTagInfo } from '../swap-or-return/SwapOrReturnTypes'

export interface Product {
  modelId: number;
  code: string;
  description: string;
  manufacturerName: string;
  isKg: boolean;
}

export interface BatchWithoutBarcode {
  id: number;
  usedBarcode: string;
  customDescription: string;
  total: number;
  isKg: boolean;
}

export enum GenericApiErrors {
  Failed,
}

export enum OverviewProductStatusEnum {
  CORRECT = 'CORRETO',
  HIGHER = 'QUANTIDADE_MAIOR',
  LOWER = 'QUANTIDADE_MENOR',
  WRONG = 'PRODUTO_ERRADO',
}

export enum IntegrationFailEnum {
  NONE = 'NONE',
  PURCHASE = 'PURCHASE',
  FINANCES = 'FINANCES',
  BOTH = 'BOTH',
}

export enum ProductStatusEnum {
  CORRECT = 'CORRECT',
  DIVERGENT = 'DIVERGENT',
  WRONG = 'WRONG',
}

export enum ReceivementStatus {
  WAITING_QUEUE = 0,
  IN_ATTENDANCE = 1,
  RECEIVED = 2,
  DIVERGENCE_OPENED = 3,
  SALES_APPROVAL_OPENED = 4,
  DIVERGENCE_FINISHED = 5,
  SALES_APPROVAL_FINISHED = 6,
  IN_BARCODE_RESOLUTION = 7,
  FAILED_WITH_EXTERNAL_SOURCES = 8,
  FINISHED = 9,
  SENT_TO_DOCK = 10,
  PRE_QUEUE = 11,
  WAITING_MANAGER_CONFERENCE = 12,
  WAITING_TRUCK_DRIVER_CONFIRMATION = 14,
  PROCESSING_TRUCK_DRIVER_CONFIRMATION = 15,
  SENDING_INVOICES_TO_FINANCES = 16,
  PROCESSING_AFTER_OVERVIEW = 17,
  CANCELED = 99,
}

export interface Batch {
  acceptedQuantity: number;
  damageQuantity: number;
  damageType: string;
  expirationDate: string;
  minExpirationDate: string;
  salesData: boolean;
  idBatch: number;
  idBatchItemOrder: number;
  isExceeding: boolean;
  usefulQuantity: number;
  totalQuantity: number;
  measureUnit: { isKg: boolean };
  selectedAction: string;
  createdAtDate: string;
}

export interface GroupedBatch {
  acceptedQuantity: number;
  damageQuantity: number;
  damageType: string;
  expirationDate: string;
  minExpirationDate: string;
  salesData: boolean;
  idsBatch: number[];
  idsBatchItemOrder: number[];
  isExceeding: boolean;
  usefulQuantity: number;
  totalQuantity: number;
  measureUnit: { isKg: boolean };
  createdAtDate: string;
}

export interface ReceivementProduct {
  acceptedQuantity: number;
  damageQuantity: number;
  expectedQuantity: number;
  idItemOrder: number;
  idProductDivergence: number;
  idReceivementAuthorization: number;
  invoiceNumber: number;
  isErpAdjusted: boolean;
  isKg: boolean;
  productCode: string;
  productDescription: string;
  status: OverviewProductStatusEnum;
  usefulQuantity: number;
  quantityPerBox: number;
  buyerName: string;
  batches: Batch[];
  modelId: number;
  orderBuyerName: string;
  hasReturn: boolean
  returnQuantity: number
  rejectedArQuantity?: number
  rejectedExcessQuantity?: number
}

export interface Overview {
  dock: string;
  operator: string;
  supervisor: string;
  supplier: string;
  duration?: string;
  invoices?: string[];
  orderBuyers?: string[];
  receivementStartDate?: Date;
  products: ReceivementProduct[];
  allowInvoiceReturn: null;
  allowPartialReturn: null;
  allowBreakUnitOnInvoiceReturn: null;
  allowProductExchange: null;
}

export type SortValuesType =
  | 'invoice_number'
  | 'product_description'
  | 'product_code'
  | 'id_lote'
  | 'status';

export type SalesDropdownOptions = keyof typeof dropdownOptions;

export type SalesReceivementBatch = GroupedBatch & {
  acceptDamage?: boolean;
  selectedOption?: SalesDropdownOptions;
};

export interface SalesReceivementProduct extends Omit<ReceivementProduct, 'batches'> {
  batches: SalesReceivementBatch[];
  observation?: string;
  quantityPerBox: number;
  quantityAcceptedIsValid?: boolean;
  currentStockDays: number;
  salesPerDay: number;
  totalReceivementStockDays: number;
  totalArStockDays: number;
  isRejected?: boolean;
}

export interface SalesOverview extends Omit<Overview, 'products'> {
  products: SalesReceivementProduct[];
}

export interface SalesOverviewResponse {
  dtReceivementStart: string;
  statusReceivement: string;
  buyerName: string;
  data: SalesOverview;
}

export interface SalesBatchesValue {
  batchItemOrderIds: number[];
  acceptedQuantity: number;
  acceptDamage: boolean;
  quantityPerBox: number;
  idReceivementAuthorization: number;
  modelId: number;
  option?: SalesDropdownOptions;
  returnQuantity?: number;
  swapQuantity?: number;
  stockDays?: number;
}

export interface SalesBatchesState {
  [key: string]: SalesBatchesValue;
}

export interface ISortOptions {
  value: SortValuesType;
  label: string;
}

export interface IOverviewFilters {
  onlyDivergents: boolean;
  productDescription: string;
  sortBy: SortValuesType;
}

export interface ISalesOverviewFilters {
  onlyDivergents?: boolean;
  productDescription?: string;
}

export interface Observation {
  observation: string;
}

export interface ReceivementState {
  overviewFilters: IOverviewFilters;
  overview?: Overview;
  wrongProductsOverview: WrongProductsOverview;
  finish?: Finish;
  loading: boolean;
  observation: Observation;
  observationsPositionByModelId: { [key: number]: number };
  resetObservations: boolean;
  batchErrorIds: string[];
  batchesStateByBatchItemOrderIds: SalesBatchesState;
  userAssigned: string;
  awaitStatus?: string;
  salesOverview?: SalesOverview;
  isPanic?: boolean;
  receivementInvoices?: ReceivementInvoices;
  receivementInvoicesHtml?: ReceivementInvoicesHtml;
  currentPrintInvoicesStep: PrintInvoicesStepsEnum;
  batches: ReceivementBatches[];
  directGondolaPrintLimit: number;
  previousPendingRequestInfo: PreviousPendingRequestInfo;
  purchaseApproval?: boolean;
}

export interface OnlyDivergentsFilter {
  onlyDivergents: IOverviewFilters['onlyDivergents'];
}

export interface ProductDescriptionFilter {
  productDescription: IOverviewFilters['productDescription'];
}

export interface SortByFilter {
  sortBy: IOverviewFilters['sortBy'];
}

export interface SetOverviewPayload {
  overview: ReceivementState['overview'];
}

export interface ConfirmTruckDriverPayload {
  idReceivement?: string;
  isAvailable: boolean;
  setDisableConfirmTruckDriver: React.Dispatch<React.SetStateAction<boolean>>
}

export interface SetSalesOverviewPayload {
  key: keyof ReceivementState['salesOverview'];
  value: ReceivementState['salesOverview'][keyof ReceivementState['salesOverview']];
}

export interface SetReceivementPayload {
  key: keyof ReceivementState;
  value: ReceivementState[keyof ReceivementState];
}

export interface SetSalesOverviewObservationPayload {
  id: number;
  observation: string;
}
export interface setActionForAllProductsPayload {
  idReceivementAuthorization: number,
  option: string
}

export interface setRejectAllProductsPayload extends Omit<setActionForAllProductsPayload, 'option'> {}

export interface SetQuantityAcceptedIsValidPayload {
  modelId: number;
  quantityAcceptedIsValid: boolean
}
export interface GetOverviewPayload extends IOverviewFilters {
  badge: string;
  receivementId: string;
}

export interface GetSalesOverviewPayload extends ISalesOverviewFilters {
  receivementId?: string;
}

export interface PatchBatchObservationPayload {
  observation: string;
  batchId: number;
}

export interface PatchItemArObservationPayload {
  observation: string;
  itemArId: number;
}

export interface GetReceivementWrongProductsFinishPayload {
  badge: string;
  receivementId: string;
}

export interface GetFinishPayload {
  badge: string;
  receivementId: string;
  productDescription?: string;
  optionalActionOnSuccess?: (data?: Finish) => void;
  onFinishRequest?: () => void;
}

export interface GetReceivementWrongProductsPayload
  extends Omit<GetOverviewPayload, 'onlyDivergents'> {}

export interface WrongProduct {
  modelId: number;
  batchId: number;
  createdAt: dayjs.Dayjs;
  expirationDate: dayjs.Dayjs;
  damageType: AvariaEnum;
  damageQuantity: number;
  measureUnit: { isKg: boolean };
  description: string;
  barcode: string;
  total: number;
  isDirectShelf: boolean;
  warehouse: string;
  street: string;
  addressTag: string;
  shelfPallet: unknown;
  binPosition: unknown;
  addressNotGondola: unknown;
  productCode: string;
  fc: { name: string };
}

export interface ReceivementBatchFinish {
  batchId: number;
  totalQuantity: number;
  damageQuantity: number;
  acceptedQuantity: number;
  swapQuantity: number;
  isKg: boolean;
}

export interface ReceivementProductFinish {
  modelId: number;
  productDescription: string;
  observations: string[];
  batches: ReceivementBatchFinish[];
}

export type WrongProductsOverview = WrongProduct[];

export interface Finish {
  dock: string;
  operator: string;
  supervisor: string;
  supplier: string;
  divergentProducts: ReceivementProductFinish[];
  correctProducts: ReceivementProductFinish[];
  receivementStatus: ReceivementStatus;
  receivementStatusDescription: string;
  idSupplier: number;
  requestsTagsInfo: RequestTagInfo[];
  idReceivementCategory: number;
  isTruckDriverAvailableReturn?: boolean;
  isRelease?: boolean;
}

export enum ReceivementCategoryEnum {
  NORMAL = 1,
  EXTERNAL_PURCHASE = 2,
  TRANSFER = 3
}

export interface SetWrongProductsOverview {
  wrongProductsOverview: ReceivementState['wrongProductsOverview'];
}

export interface SetInvoicesReturn {
  invoicesReturn: ReceivementInvoices['invoicesReturn'];
}

export interface SetInvoicesReturnHtml {
  returnInvoicesHtml: ReceivementInvoicesHtml['returnInvoicesHtml'];
}

export interface SetFinish {
  finish: ReceivementState['finish'];
}

export interface PatchResponse {
  status: string;
}

export interface CloseSalesAdjustmentPayload {
  receivementId: string;
  data: SalesBatchesValue[];
  history: History
}

export interface Invoice {
  isSigned: boolean | null
  idInvoicePurchase: number
  invoiceAccessKey: string
  invoiceNumber: number
  invoiceIssueDate: Date
  invoiceTotalValue: number
  invoiceShopperDocument: string
  invoiceIssuerDocument: string
  invoiceIssuerName: string
  idSupplier: number
}

export interface ReturnInvoice {
  id: number
  dtCreated: Date
  idUsrCreated: number
  invoiceKeyOrigin: string
  invoiceKeyReturn: string
  status: string
  idReceivement: number
  invoiceNumber: number
  invoiceValue: number
  emissionDate: Date
}

export enum EnumStatusReturnInvoice {
  APPROVED = 'Aprovada',
  PENDING = 'Pendente',
  REJECTED = 'Rejeitada',
  INVALID = 'Inválida',
  PROCESSING = 'Processando',
  CANCELED = 'Cancelada',
  SEFAZ_APPROVED = 'Aprovada pela SEFAZ',
  CONTINGENCY_APPROVED = 'Aprovado por contingência',
  APPROVED_BY_FINANCES = 'Aprovado pelo Financeiro',
  REPROVED_BY_FINANCES = 'Reprovado pelo Financeiro',
}

export interface ReceivementInvoices {
  idReceivement: number
  idReceivementStatus: ReceivementStatus
  integrationFail: IntegrationFailEnum
  invoices: Invoice[]
  invoicesRejected: Invoice[]
  invoicesReturn: ReturnInvoice[]
  failedInvoices: Invoice[]
  alreadyReleased: boolean
  isTruckDriverAvailableReturn?: boolean
}

export interface ReceivementInvoicesHtml {
  idReceivement: number
  idReceivementStatus: ReceivementStatus
  integrationFail: IntegrationFailEnum
  invoices: Invoice[]
  acceptedInvoicesHtml: string[]
  returnInvoicesHtml: string[]
  rejectedInvoicesHtml: string[]
}

export interface SetReceivementInvoices {
  receivementInvoices: ReceivementState['receivementInvoices'];
}

export interface SetReceivementInvoicesHtml {
  receivementInvoicesHtml: ReceivementState['receivementInvoicesHtml'];
}

export interface SetBatches {
  batches: ReceivementState['batches'];
}

export interface SetDirectGondolaPrintLimit {
  directGondolaPrintLimit: ReceivementState['directGondolaPrintLimit'];
}

export interface GetReceivementInvoicesPayload {
  badge: string;
  attendancePassword: string;
}

export interface PdfFile {
  invoiceKeyReturn: string;
  fileName: string;
  pdfFile: File;
}

export interface UploadReceiptsPayload {
  pdfFiles: PdfFile[],
  idReceivement: number,
  setNextButtonIsDisabled: (disabled: boolean) => void,
}

export interface SendJustificationReceiptsPayload {
  justification: string,
  idReceivement: number,
  setNextButtonIsDisabled: (disabled: boolean) => void,
}

export interface ReleaseReceivementPayload {
  idReceivement?: number,
  badge: number
}

export interface ResendToFinances {
  idReceivement?: number;
  chaveNf?: string;
}

export interface CheckStatus {
  idReceivement?: number;
}

export interface GetBatches {
  receivementId: number;
  badge: string;
}

export interface ReceivementBatches {
  idLote: number;
  batchNumber: number;
  idModelo: number;
  codigoDeBarrasUtilizado: string;
  descricao: string;
  addressDetail: {
    warehouse: string;
    aisle: string;
    shelfPallet: string;
    binPosition: string;
  }
  fc: {
    name: string;
  }
  codProduto: string;
  diretoGondola: boolean;
  dataInclusao: Date;
  dataEntrada: Date;
  addressTag: {
      enderecoGenerated: string;
  }
  suggestedStorageReceivement: string;
  printedDirectGondola: boolean;
}

export interface GetReceivementBatches {
  data: ReceivementBatches[];
  directGondolaPrintLimit: number;
}

export enum InfoSupplier {
  NOT_ACCEPT_NFD = 'NÃO ACEITA NFD',
  ACCEPT_NFD = 'ACEITA NFD',
  RETURN_BY_BOXES = 'ACEITA NFD: somente CAIXARIA',
  RETURN_BY_UNIT = 'ACEITA NFD: com UNIDADES avulsas',
}

export interface InfoReturnOptions {
  allowBreakUnitOnInvoiceReturn: boolean | null,
  allowInvoiceReturn: boolean | null,
  allowPartialReturn: boolean | null,
  allowProductExchange: boolean | null,
}

export interface SetProperty {
  property: keyof ReceivementState
  value: ReceivementState[keyof ReceivementState]
}

export interface GetPreviousPendingRequestsPayload {
  idSupplier: number
  idFulfillmentCenter: number
}

export interface DetailsPreviousPendingRequest {
  productDescription: string
  productCode: string
  returnQuantity: number
}

export interface PreviousPendingRequest {
  requestId: number,
  invoiceNumber: number,
  invoiceKey: string,
  requestTypeId: number,
  returnSkusQuantity: number,
  totalReturnVolume: number,
  receivementDate: Date,
  details: DetailsPreviousPendingRequest[]
}

export interface PreviousPendingRequestInfo {
  supplierName: string
  requests: PreviousPendingRequest[]
}

export interface setTotalReceivementStockDaysPayload {
  batchesState: SalesBatchesState
}

export interface setTotalArStockDaysPayload {
  modelId: number
  idReceivementAuthorization: number
  totalArStockDays: number
}

export interface GetReceivementInvoicesByReceivementOrRequestPayload {
  idReceivement: number
  idRequest: number
}

export interface ReleaseReceivementSwapReturnPayload {
  idReceivement: number
  onSuccess: () => void
}

export interface ValidadeProductBarcodePayload {
  barcode: string
  idModel: number
  onSuccess: () => void
}

export interface SignAuthorizationPayload {
  accessKey: string
  onSuccess: () => void
}

export interface SetInvoiceSignedPayload {
  accessKey: string
  isSigned: boolean
}

export interface SignAuthorizationData {
  isSigned: boolean
}
